import React, { useState, memo } from 'react';
import { Download } from 'lucide-react';

interface AlbumMessageProps {
    albumText: string;
    onOpenViewer: (images: string[], currentIndex: number) => void;
}

/**
 * AlbumMessage Component
 * Displays a grid of images for album messages
 * Format: [ALBUM:url1|url2|url3|...]
 * Memoized to prevent unnecessary re-renders
 */
const AlbumMessage = memo(function AlbumMessage({ albumText, onOpenViewer }: AlbumMessageProps) {
    const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());

    // Parse album URLs from text format [ALBUM:url1|url2|url3]
    const parseAlbumUrls = (text: string): string[] => {
        if (text.startsWith('[ALBUM:') && text.endsWith(']')) {
            const urlsString = text.substring(7, text.length - 1); // Remove [ALBUM: and ]
            return urlsString.split('|').filter((url) => url.trim() !== '');
        }
        return [];
    };

    const photoUrls = parseAlbumUrls(albumText);

    if (photoUrls.length === 0) {
        return null;
    }

    const handleImageLoad = (index: number) => {
        setLoadedImages((prev) => new Set(prev).add(index));
    };

    const handleImageClick = (index: number) => {
        onOpenViewer(photoUrls, index);
    };

    const handleDownload = async (url: string, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `image-${Date.now()}.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Failed to download image:', error);
        }
    };

    // Render single photo
    if (photoUrls.length === 1) {
        const imageUrl = photoUrls[0];
        const isLocalFile = imageUrl.startsWith('local://');
        const actualImageUrl = isLocalFile ? imageUrl.substring(8) : imageUrl;

        return (
            <div
                className="relative max-w-[280px] max-h-[420px] rounded-lg overflow-hidden cursor-pointer group"
                onClick={() => handleImageClick(0)}
            >
                <img
                    src={actualImageUrl}
                    alt="Album"
                    className="w-full h-full object-cover"
                    onLoad={() => handleImageLoad(0)}
                />
                {!isLocalFile && (
                    <button
                        onClick={(e) => handleDownload(actualImageUrl, e)}
                        className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                        <Download size={16} className="text-white" />
                    </button>
                )}
                {!loadedImages.has(0) && (
                    <div className="absolute inset-0 bg-gray-200 animate-pulse" />
                )}
            </div>
        );
    }

    // Render photo grid for multiple images
    const getGridLayout = (count: number) => {
        if (count === 2) {
            return {
                cols: 2,
                rows: 1,
                gridClass: 'grid-cols-2',
                aspectRatio: '1/1'
            };
        } else if (count === 3) {
            return {
                cols: 2,
                rows: 2,
                gridClass: 'grid-cols-2',
                aspectRatio: '1/1'
            };
        } else {
            return {
                cols: 2,
                rows: 2,
                gridClass: 'grid-cols-2',
                aspectRatio: '1/1'
            };
        }
    };

    const layout = getGridLayout(photoUrls.length);
    const displayCount = Math.min(photoUrls.length, 4); // Max 4 thumbnails

    return (
        <div
            className={`grid ${layout.gridClass} gap-0.5 w-full max-w-[280px] rounded-lg overflow-hidden`}
            style={{ backgroundColor: '#e5e7eb' }}
        >
            {photoUrls.slice(0, displayCount).map((url, index) => {
                const isLocalFile = url.startsWith('local://');
                const actualImageUrl = isLocalFile ? url.substring(8) : url;
                const isLastTile = index === 3 && photoUrls.length > 4;

                return (
                    <div
                        key={index}
                        className="relative cursor-pointer group overflow-hidden"
                        style={{ aspectRatio: layout.aspectRatio }}
                        onClick={() => handleImageClick(index)}
                    >
                        <img
                            src={actualImageUrl}
                            alt={`Album ${index + 1}`}
                            className="w-full h-full object-cover"
                            onLoad={() => handleImageLoad(index)}
                        />
                        {!isLocalFile && (
                            <button
                                onClick={(e) => handleDownload(actualImageUrl, e)}
                                className="absolute top-1 right-1 p-1.5 bg-black/50 hover:bg-black/70 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
                            >
                                <Download size={14} className="text-white" />
                            </button>
                        )}
                        {!loadedImages.has(index) && (
                            <div className="absolute inset-0 bg-gray-200 animate-pulse" />
                        )}
                        {isLastTile && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                <span className="text-white text-2xl font-bold">
                                    +{photoUrls.length - 4}
                                </span>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
});

export default AlbumMessage;
